import { DateUtils } from "@repo/common"
import { Button } from "@repo/ui/components/ui/button"
import { Calendar } from "@repo/ui/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/ui/popover"
import { useDateFnsLocale } from "@repo/ui/lib/i18n"
import { cn } from "@repo/ui/lib/utils"
import { CalendarIcon, X } from "lucide-react"
import React from "react"
import { useState } from "react"
import { Matcher } from "react-day-picker"
import { useTranslations } from "use-intl"

type CalendarProps = React.ComponentProps<typeof Calendar>

interface DatePickerProps {
  value: Date | undefined
  onChange: (date: Date | undefined) => void
  placeholder?: string
  formatValue?: (date: Date) => string
  disabled?: Matcher | Matcher[]
  /** Disables the trigger button so the calendar can't be opened at all. */
  isTriggerDisabled?: boolean
  fromDate?: Date
  toDate?: Date
  className?: string
  align?: "start" | "center" | "end"
  clearable?: boolean
  calendarProps?: Partial<
    Omit<
      CalendarProps,
      | "mode"
      | "selected"
      | "onSelect"
      | "disabled"
      | "fromDate"
      | "toDate"
      | "locale"
      | "weekStartsOn"
    >
  >
}

function DatePicker({
  value,
  onChange,
  placeholder,
  formatValue,
  disabled,
  isTriggerDisabled = false,
  fromDate,
  toDate,
  className,
  align = "start",
  clearable = false,
  calendarProps,
}: DatePickerProps) {
  const t = useTranslations("ui")
  const dateFnsLocale = useDateFnsLocale()
  const [open, setOpen] = useState(false)

  const displayValue = (date: Date) => {
    if (formatValue) return formatValue(date)
    return DateUtils.formatDate(date)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={isTriggerDisabled}
          className={cn(
            "w-full pl-3 text-left font-normal",
            !value && "text-muted-foreground",
            className,
          )}
        >
          {value ? (
            displayValue(value)
          ) : (
            <span>{placeholder ?? t("common.chooseDate")}</span>
          )}
          <div className="ml-auto flex items-center gap-1">
            {clearable && value && (
              <span
                role="button"
                tabIndex={0}
                className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(undefined)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation()
                    onChange(undefined)
                  }
                }}
                aria-label={t("datePicker.clearDate")}
              >
                <X className="h-3 w-3" />
              </span>
            )}
            <CalendarIcon className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          {...calendarProps}
          mode="single"
          selected={value}
          onSelect={(date) => {
            if (date) {
              const normalized = new Date(date)
              normalized.setHours(12, 0, 0, 0)
              onChange(normalized)
            } else {
              onChange(undefined)
            }
            setOpen(false)
          }}
          disabled={disabled}
          fromDate={fromDate}
          toDate={toDate}
          locale={dateFnsLocale}
          weekStartsOn={1}
        />
      </PopoverContent>
    </Popover>
  )
}
DatePicker.displayName = "DatePicker"

export { DatePicker }
export type { DatePickerProps }
